import { useState } from 'react'
import {
  Ban,
  Bell,
  BookOpen,
  Calendar,
  FileCheck,
  FileText,
  Pencil,
  Plus,
  CreditCard,
  TrendingUp,
  X,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { cn } from '@/utils/cn'
import { Button, FormInput } from '@/components'
import { ROUTES } from '@/constants'

const STAT_CARDS = [
  {
    title: 'Upcoming Class',
    value: 'Today, 4:30pm',
    valueColor: 'text-amber-500',
    icon: Calendar,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-500',
  },
  {
    title: 'Active Courses',
    value: '3',
    valueColor: 'text-gray-900',
    icon: BookOpen,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    title: 'Certificate Earned',
    value: '5',
    valueColor: 'text-gray-900',
    icon: FileCheck,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  {
    title: 'Pending Payment',
    value: '$150',
    valueColor: 'text-gray-900',
    icon: CreditCard,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-500',
  },
] as const

const MOCK_CHILDREN = [
  {
    id: '1',
    name: 'Sarah Johnson',
    grade: 'Grade 5th',
    code: 'CAABCD12',
    avatar: null,
    courses: [
      'Python for Beginners',
      'Private 1-on- 1 Flexible - Wed & Fri, 4:30 PM',
    ],
    level: 'Gold Level',
    status: 'Active',
    progress: 55,
  },
  {
    id: '2',
    name: 'Michael Johnson',
    grade: 'Grade 5th',
    code: 'CAABCD12',
    avatar: null,
    courses: ['Web Development'],
    level: 'Gold Level',
    status: 'Active',
    progress: 55,
  },
] as const

type Child = (typeof MOCK_CHILDREN)[number]

function ProgressRing({ value }: { value: number }) {
  const size = 80
  const stroke = 7
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const offset = circumference - (value / 100) * circumference
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#34A853"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <span className="absolute text-sm font-semibold text-gray-800">
        {value}%
      </span>
    </div>
  )
}

export const ParentDashboard: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [editingChild, setEditingChild] = useState<Child | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    grade: '',
    code: '',
  })
  const [age, setAge] = useState('')
  const [gender, setGender] = useState<'Male' | 'Female' | 'Other' | ''>('')
  const [selectedAvatar, setSelectedAvatar] = useState(0)
  const [isAddChildOpen, setIsAddChildOpen] = useState(false)
  const [newChildAvatar, setNewChildAvatar] = useState(0)
  const [newChildForm, setNewChildForm] = useState({
    name: '',
    age: '',
    grade: '',
    gender: '' as '' | 'Male' | 'Female' | 'Other',
    notes: '',
  })

  const openEditModal = (child: Child) => {
    setEditingChild(child)
    setEditForm({
      name: child.name,
      grade: child.grade,
      code: child.code,
    })
    setAge('')
    setGender('')
    setSelectedAvatar(0)
  }

  const closeEditModal = () => {
    setEditingChild(null)
  }

  const handleEditFieldChange = (
    field: keyof typeof editForm,
    value: string
  ) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSaveChanges = () => {
    // TODO: Wire this up to real API / state update
    setEditingChild(null)
  }

  const displayName = user ? `${user.firstName} ${user.lastName}` : 'Mrs. Ada'

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Welcome, {displayName} 👋
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Here&apos;s what&apos;s happening with your children&apos;s learning
            journey
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
          </button>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(card => {
          const Icon = card.icon
          return (
            <div
              key={card.title}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4"
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
                  card.iconBg
                )}
              >
                <Icon className={cn('w-5 h-5', card.iconColor)} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-400">
                  {card.title}
                </p>
                <p
                  className={cn(
                    'text-base font-semibold mt-0.5',
                    card.valueColor
                  )}
                >
                  {card.value}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* My Children */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-5">
          <h2 className="text-lg font-semibold text-gray-800">My Children</h2>
          <Button
            type="button"
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            className="bg-[#F28C28] hover:bg-amber-600 focus:ring-amber-500 text-sm py-2.5 px-4 rounded-xl"
            onClick={() => {
              setIsAddChildOpen(true)
              setNewChildAvatar(0)
              setNewChildForm({
                name: '',
                age: '',
                grade: '',
                gender: '',
                notes: '',
              })
            }}
          >
            Add Child
          </Button>
        </div>

        <div className="space-y-5">
          {MOCK_CHILDREN.map(child => (
            <div
              key={child.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Avatar + Info */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center shrink-0 overflow-hidden">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-base font-bold text-amber-700">
                      {child.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-base">
                      {child.name}
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {child.grade} · Code: {child.code}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {child.courses.map(course => (
                        <li
                          key={course}
                          className="flex items-center gap-2 text-xs text-gray-600"
                        >
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          {course}
                        </li>
                      ))}
                    </ul>
                    <span className="inline-block mt-2 px-2.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-medium">
                      {child.level}
                    </span>
                  </div>
                </div>

                {/* Status + Progress */}
                <div className="flex flex-col items-end gap-3 shrink-0">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-100">
                    {child.status}
                  </span>
                  <ProgressRing value={child.progress} />
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-50">
                <Button
                  type="button"
                  variant="primary"
                  icon={<TrendingUp className="w-4 h-4" />}
                  className="bg-[#34A853] hover:bg-emerald-600 focus:ring-emerald-500 text-sm py-2"
                  onClick={() =>
                    navigate(ROUTES.PARENT.PROGRESS, {
                      state: {
                        child: {
                          id: child.id,
                          name: child.name,
                          grade: child.grade,
                          code: child.code,
                        },
                      },
                    })
                  }
                >
                  View Progress
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  icon={<FileText className="w-4 h-4" />}
                  className="border-gray-200 text-gray-600 hover:bg-gray-50 text-sm py-2"
                  onClick={() =>
                    navigate(ROUTES.PARENT.REPORTS, {
                      state: {
                        child: {
                          id: child.id,
                          name: child.name,
                          grade: child.grade,
                          code: child.code,
                        },
                      },
                    })
                  }
                >
                  Reports
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  icon={<Pencil className="w-4 h-4" />}
                  className="border-gray-200 text-gray-600 hover:bg-gray-50 text-sm py-2"
                  onClick={() => openEditModal(child)}
                >
                  Edit Profile
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  icon={<Ban className="w-4 h-4" />}
                  className="bg-red-500 hover:bg-red-600 focus:ring-red-500 text-sm py-2"
                >
                  Disable Login
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {isAddChildOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 sm:px-6">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsAddChildOpen(false)}
          />
          <div className="relative z-50 w-full max-w-xl">
            <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.45)]">
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 pt-5 pb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add New Child
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Add a new child to your account and auto-generate their
                    login credentials.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddChildOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="px-6 pb-6 pt-4 space-y-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Select avatar
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setNewChildAvatar(index)}
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-full border-2 bg-amber-50 text-xs font-semibold text-amber-700 shadow-sm transition',
                          newChildAvatar === index
                            ? 'border-amber-500 ring-2 ring-amber-200'
                            : 'border-transparent hover:border-amber-300'
                        )}
                      >
                        {newChildForm.name
                          ? newChildForm.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                          : 'A'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormInput
                    label="Child's name"
                    value={newChildForm.name}
                    onChange={e =>
                      setNewChildForm(prev => ({
                        ...prev,
                        name: e.currentTarget.value,
                      }))
                    }
                    placeholder="Jane Doe"
                    required
                    wrapperClassName="sm:col-span-2"
                  />
                  <FormInput
                    label="Age"
                    type="number"
                    value={newChildForm.age}
                    onChange={e =>
                      setNewChildForm(prev => ({
                        ...prev,
                        age: e.currentTarget.value,
                      }))
                    }
                    placeholder="10"
                  />
                  <FormInput
                    label="Grade"
                    value={newChildForm.grade}
                    onChange={e =>
                      setNewChildForm(prev => ({
                        ...prev,
                        grade: e.currentTarget.value,
                      }))
                    }
                    placeholder="Grade 5th"
                  />
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 tracking-tight">
                    Select gender
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['Male', 'Female', 'Other'].map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() =>
                          setNewChildForm(prev => ({
                            ...prev,
                            gender: option as 'Male' | 'Female' | 'Other',
                          }))
                        }
                        className={cn(
                          'inline-flex items-center justify-center rounded-full border px-4 py-1.5 text-xs font-medium transition',
                          newChildForm.gender === option
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50/60'
                        )}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Notes (optional)
                  </label>
                  <textarea
                    rows={4}
                    value={newChildForm.notes}
                    onChange={e =>
                      setNewChildForm(prev => ({
                        ...prev,
                        notes: e.currentTarget.value,
                      }))
                    }
                    placeholder="Add any additional information you want us to know."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-portal-orange focus:bg-white focus:ring-1 focus:ring-portal-orange/40"
                  />
                </div>

                <div className="mt-2 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsAddChildOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="rounded-xl bg-[#F28C28] px-5 py-2.5 text-sm font-semibold shadow-[0_14px_30px_rgba(242,140,40,0.35)] hover:bg-amber-600 focus:ring-amber-500"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {editingChild && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 sm:px-6">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeEditModal}
          />
          <div className="relative z-50 w-full max-w-xl">
            <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.45)]">
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 pt-5 pb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Child Profile
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Add or update your child&apos;s details and we&apos;ll keep their
                    login credentials in sync.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="px-6 pb-6 pt-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Select avatar
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSelectedAvatar(index)}
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-full border-2 bg-amber-50 text-xs font-semibold text-amber-700 shadow-sm transition',
                          selectedAvatar === index
                            ? 'border-amber-500 ring-2 ring-amber-200'
                            : 'border-transparent hover:border-amber-300'
                        )}
                      >
                        {editingChild.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormInput
                    label="Child's full name"
                    value={editForm.name}
                    onChange={e =>
                      handleEditFieldChange('name', e.currentTarget.value)
                    }
                    placeholder="Enter full name"
                    required
                    wrapperClassName="sm:col-span-2"
                  />
                  <FormInput
                    label="Age"
                    type="number"
                    value={age}
                    onChange={e => setAge(e.currentTarget.value)}
                    placeholder="e.g. 10"
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 tracking-tight">
                      Grade / Class
                    </label>
                    <select
                      value={editForm.grade}
                      onChange={e =>
                        handleEditFieldChange('grade', e.currentTarget.value)
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-[15px] text-gray-900 shadow-sm transition focus:border-portal-orange focus:bg-white focus:outline-none focus:ring-1 focus:ring-portal-orange/40"
                    >
                      <option value="Grade 3rd">Grade 3rd</option>
                      <option value="Grade 4th">Grade 4th</option>
                      <option value="Grade 5th">Grade 5th</option>
                      <option value="Grade 6th">Grade 6th</option>
                      <option value={editForm.grade}>Other</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 tracking-tight">
                    Select gender
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['Male', 'Female', 'Other'].map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() =>
                          setGender(option as 'Male' | 'Female' | 'Other')
                        }
                        className={cn(
                          'inline-flex items-center justify-center rounded-full border px-4 py-1.5 text-xs font-medium transition',
                          gender === option
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50/60'
                        )}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <FormInput
                    label="Student code"
                    value={editForm.code}
                    onChange={e =>
                      handleEditFieldChange('code', e.currentTarget.value)
                    }
                    placeholder="Unique student code"
                    helperText="This will be used as part of the child’s login credentials."
                  />
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-amber-500">
                      Student Code
                    </span>
                    <span className="text-[13px] font-semibold tracking-[0.24em]">
                      {editingChild.code}
                    </span>
                  </div>
                </div>

                <p className="mt-4 text-xs text-gray-400">
                  Login credentials will be updated automatically once you save
                  these changes.
                </p>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    onClick={closeEditModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="rounded-xl bg-[#F28C28] px-5 py-2.5 text-sm font-semibold shadow-[0_14px_30px_rgba(242,140,40,0.35)] hover:bg-amber-600 focus:ring-amber-500"
                    onClick={handleSaveChanges}
                  >
                    Update Child Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
